import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Flame, Clock, Users, Bookmark, BookmarkCheck, CalendarPlus, Leaf, Eye, Camera, Timer } from "lucide-react";

const DIFF_COLORS = {
  Easy: "#4A6B4E",
  Medium: "#D49B4B",
  Hard: "#C85A32",
};

export default function RecipeCard({ recipe, onView, onSave, onAddPlan, isSaved, autoImage = true }) {
  const [image, setImage] = useState(recipe.image_url || null);
  const [loadingImg, setLoadingImg] = useState(false);
  const requested = useRef(false);

  const fetchImage = async () => {
    if (requested.current || image) return;
    requested.current = true;
    setLoadingImg(true);
    try {
      const res = await api.post("/recipes/image", {
        title: recipe.title,
        description: recipe.description,
      });
      setImage(res.data.image_url);
      recipe.image_url = res.data.image_url;
    } catch (e) {
      // leave placeholder
    } finally {
      setLoadingImg(false);
    }
  };

  useEffect(() => {
    if (autoImage && !recipe.image_url) fetchImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const n = recipe.nutrition || {};

  return (
    <div
      className="bg-white rounded-2xl border border-[#E2D9CC] overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col animate-fade-up"
      data-testid={`recipe-card-${recipe.id}`}
    >
      <div className="relative h-48 bg-[#F4EFE6] overflow-hidden">
        {image ? (
          <img src={image} alt={recipe.title} className="w-full h-full object-cover" />
        ) : loadingImg ? (
          <div className="w-full h-full shimmer flex items-center justify-center">
            <Camera className="w-6 h-6 text-[#C5B8A5]" />
          </div>
        ) : (
          <button onClick={fetchImage} className="w-full h-full flex flex-col items-center justify-center text-[#9C8F85] gap-1">
            <Camera className="w-6 h-6" />
            <span className="text-xs">Generate photo</span>
          </button>
        )}
        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
          {(recipe.dietary_tags || []).slice(0, 2).map((t) => (
            <span key={t} className="px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-mono uppercase tracking-wider text-[#4A6B4E] font-medium">
              {t}
            </span>
          ))}
        </div>
        {recipe.difficulty && (
          <span
            className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider text-white font-medium"
            style={{ background: DIFF_COLORS[recipe.difficulty] || "#6E6259" }}
          >
            {recipe.difficulty}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-serif text-xl leading-snug">{recipe.title}</h3>
        <p className="text-sm text-[#6E6259] mt-1.5 line-clamp-2">{recipe.description}</p>

        <div className="flex items-center gap-4 mt-4 text-xs text-[#9C8F85]">
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{(recipe.prep_time || 0) + (recipe.cook_time || 0)}m</span>
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{recipe.servings || 2}</span>
          <span className="flex items-center gap-1 text-[#C85A32] font-medium"><Flame className="w-3.5 h-3.5" />{n.calories || "–"} kcal</span>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4">
          {[["Protein", n.protein, "#4A6B4E"], ["Carbs", n.carbs, "#D49B4B"], ["Fat", n.fat, "#C85A32"]].map(([l, v, c]) => (
            <div key={l} className="text-center rounded-xl bg-[#FBF9F5] py-2 border border-[#E2D9CC]">
              <div className="font-serif text-lg" style={{ color: c }}>{v ?? "–"}<span className="text-xs">g</span></div>
              <div className="text-[10px] text-[#9C8F85] uppercase tracking-wide">{l}</div>
            </div>
          ))}
        </div>

        {recipe.rescues?.length > 0 && (
          <div data-testid={`rescues-${recipe.id}`} className="mt-3 flex items-center gap-1.5 text-xs text-[#4A6B4E] bg-[#E8F0E6] rounded-lg px-3 py-2">
            <Timer className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate"><b>Rescues:</b> {recipe.rescues.join(", ")}</span>
          </div>
        )}

        {recipe.missing_ingredients?.length > 0 && (
          <div className="mt-3 text-xs text-[#B04B26] bg-[#FDF5E6] rounded-lg px-3 py-2">
            Missing {recipe.missing_ingredients.length}: {recipe.missing_ingredients.slice(0, 3).join(", ")}
            {recipe.missing_ingredients.length > 3 ? "…" : ""}
          </div>
        )}

        {recipe.zero_waste_tip && (
          <div className="mt-3 flex items-start gap-1.5 text-xs text-[#4A6B4E]">
            <Leaf className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="line-clamp-2">{recipe.zero_waste_tip}</span>
          </div>
        )}

        <div className="flex items-center gap-2 mt-5 pt-4 border-t border-[#E2D9CC]">
          <button
            data-testid={`view-recipe-${recipe.id}`}
            onClick={() => onView(recipe)}
            className="flex-1 flex items-center justify-center gap-1.5 h-10 rounded-xl bg-[#2C221E] text-white text-sm hover:bg-[#3d2f28] transition-colors"
          >
            <Eye className="w-4 h-4" /> View
          </button>
          {onSave && (
            <button
              data-testid={`save-recipe-${recipe.id}`}
              onClick={() => onSave(recipe)}
              className={`h-10 w-10 rounded-xl flex items-center justify-center transition-colors ${isSaved ? "bg-[#4A6B4E] text-white" : "bg-[#F4EFE6] text-[#6E6259] hover:bg-[#E8F0E6]"}`}
              title="Save"
            >
              {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            </button>
          )}
          {onAddPlan && (
            <button
              data-testid={`plan-recipe-${recipe.id}`}
              onClick={() => onAddPlan(recipe)}
              className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#F4EFE6] text-[#6E6259] hover:bg-[#FDF5E6] transition-colors"
              title="Add to planner"
            >
              <CalendarPlus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
